import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import ClientPortalLayout from '../components/ClientPortalLayout';
import { PaymentInfoPortalTab, PaymentInfoReminder } from '../components/PaymentInfo'; // @@PAYMENT_INFO_v82@@ @@PAYMENT_INFO_v82_1@@
import InvoicesTab from '../components/InvoicesTab'; // @@PORTAL_INVOICES_v83@@
import EstimatesTab from '../components/EstimatesTab'; // @@PORTAL_ESTIMATES_v93@@
import TechPacksTab from '../components/TechPacksTab'; // @@TECH_PACKS_v94@@
import OpenInvoicesCard from '../components/OpenInvoicesCard'; // @@DASH_OPEN_INVOICES_v84@@
import LeasesTab from '../components/LeasesTab'; // @@EQUIPMENT_LEASES_v87@@

// @@CLIENT_PORTAL_v1@@
// The client portal. One page that owns the selected client (switcher) and
// renders either the Dashboard tab or the Statement tab. All data comes from
// /api/portal/* which is server-scoped to this login's clients — the client_id
// we pass is only honored if the server already allows it.

function money(n, ccy) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'USD' }).format(Number(n));
  } catch {
    return `$${Number(n).toFixed(2)}`;
  }
}
function fmtDate(s) { if (!s) return '—'; try { return new Date(s).toLocaleDateString(); } catch { return '—'; } }

// @@PERIOD_LABEL@@ Derive the period label from the reference string, NOT from
// period_start (toLocaleDateString shifts a UTC date back a day -> May shows
// 4/30). Reference is YYYY-MM-<desig> (month) or YYYY-Q#-<desig> (quarter).
const CP_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CP_QUARTER_MONTHS = { '1': 'Jan–Mar', '2': 'Apr–Jun', '3': 'Jul–Sep', '4': 'Oct–Dec' };
function periodLabel(reference, periodType) {
  const ref = String(reference || '');
  // Quarter: YYYY-Q#-...
  let m = ref.match(/^(\d{4})-Q([1-4])\b/);
  if (m) return `Q${m[2]} ${m[1]} (${CP_QUARTER_MONTHS[m[2]]})`;
  // Month: YYYY-MM-...
  m = ref.match(/^(\d{4})-(\d{2})\b/);
  if (m) {
    const mi = parseInt(m[2], 10) - 1;
    if (mi >= 0 && mi <= 11) return `${CP_MONTHS[mi]} ${m[1]}`;
  }
  // Fallback: capitalized period_type if the reference can't be parsed.
  if (periodType === 'quarter') return 'Quarter';
  if (periodType === 'month') return 'Month';
  return periodType || '—';
}

export default function ClientPortal() {
  // @@EQUIPMENT_LEASES_v87@@ ?tab= deep link (the lease email links to ?tab=equipment)
  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return ['statement', 'invoices', 'estimates', 'inventory', 'quickquote', 'payment', 'equipment', 'techpacks' /* @@TECH_PACKS_v94@@ */].includes(t) /* @@PORTAL_ESTIMATES_v93@@ +estimates */ ? t : 'dashboard';
    } catch (e) { return 'dashboard'; }
  });
  const [clients, setClients] = useState(null);   // null = loading
  const [clientId, setClientId] = useState('');
  const [err, setErr] = useState('');
  const [shippingOn, setShippingOn] = useState(false); // @@SHIPPING_TAB_v2@@
  // @@REPORT_REVIEW_v115@@ no connected Shopify store => hide the store-only sections (MTD cards, report history, Inventory tab).
  const activeClient = (clients || []).find((c) => c.id === clientId);
  const hasStore = !activeClient || activeClient.has_store !== false;
  useEffect(() => { if (!hasStore && tab === 'inventory') setTab('dashboard'); }, [hasStore, tab]);
  // @@PAYMENT_ACCESS_v125@@ back-office setting per client (Automatic follows the linked store): Payment Info tab / reminder / ?tab=payment deep link
  const needsPay = !activeClient || activeClient.needs_payment_info !== false;
  useEffect(() => { if (!needsPay && tab === 'payment') setTab('dashboard'); }, [needsPay, tab]);

  // Load the switcher list once.
  useEffect(() => {
    let active = true;
    apiFetch('/api/portal/clients')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!active) return;
        const list = (d && d.clients) || [];
        setClients(list);
        if (list.length) setClientId((cur) => cur || list[0].id);
      })
      .catch(() => { if (active) setClients([]); });
    return () => { active = false; };
  }, []);

  // @@SHIPPING_TAB_v2@@ shipping enabled for the active client? (nav gate)
  useEffect(() => {
    if (!clientId) { setShippingOn(false); return; }
    let active = true;
    apiFetch(`/api/portal/shipping?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active) setShippingOn(!!(d && d.available)); })
      .catch(() => { if (active) setShippingOn(false); });
    return () => { active = false; };
  }, [clientId]);

  // @@EQUIPMENT_LEASES_v87@@ leases for the active client: Equipment tab gate + "needs acceptance" banner.
  const [leaseInfo, setLeaseInfo] = useState({ count: 0, awaiting: 0 });
  const loadLeaseInfo = useCallback(() => {
    if (!clientId) { setLeaseInfo({ count: 0, awaiting: 0 }); return; }
    apiFetch(`/api/portal/leases?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setLeaseInfo({ count: d && d.leases ? d.leases.length : 0, awaiting: (d && d.awaiting) || 0 }))
      .catch(() => setLeaseInfo({ count: 0, awaiting: 0 }));
  }, [clientId]);
  useEffect(() => { loadLeaseInfo(); }, [loadLeaseInfo]);

  // @@PORTAL_ESTIMATES_v93@@ estimates awaiting this client's approval (banner).
  const [estAwaiting, setEstAwaiting] = useState(0);
  const loadEstInfo = useCallback(() => {
    if (!clientId) { setEstAwaiting(0); return; }
    apiFetch(`/api/portal/estimates?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setEstAwaiting((d && d.available && d.awaiting) || 0))
      .catch(() => setEstAwaiting(0));
  }, [clientId]);
  useEffect(() => { loadEstInfo(); }, [loadEstInfo]);

  // @@TECH_PACKS_v94@@ tech packs for the active client: tab gate + "needs your review" banner.
  const [tpInfo, setTpInfo] = useState({ count: 0, awaiting: 0 });
  const loadTpInfo = useCallback(() => {
    if (!clientId) { setTpInfo({ count: 0, awaiting: 0 }); return; }
    apiFetch(`/api/portal/techpacks?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setTpInfo({ count: d && d.packs ? d.packs.length : 0, awaiting: (d && d.awaiting) || 0 }))
      .catch(() => setTpInfo({ count: 0, awaiting: 0 }));
  }, [clientId]);
  useEffect(() => { loadTpInfo(); }, [loadTpInfo]);

  // @@PORTAL_PAY_v124_1@@ header "Make a Payment": shown when this client can pay (card, bank transfer or check).
  const [payOk, setPayOk] = useState(false);
  const [autoPay, setAutoPay] = useState(0);
  useEffect(() => {
    if (!clientId) { setPayOk(false); return undefined; }
    let active = true;
    apiFetch(`/api/portal/pay/options?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active) setPayOk(!!(d && d.available)); })
      .catch(() => { if (active) setPayOk(false); });
    return () => { active = false; };
  }, [clientId]);
  const startPay = useCallback(() => { setTab('invoices'); setAutoPay((n) => n + 1); }, []);
  const clearAutoPay = useCallback(() => setAutoPay(0), []);

  if (clients === null) {
    return <ClientPortalLayout tab={tab} onTab={setTab}><div className="cp-empty">Loading…</div></ClientPortalLayout>;
  }
  if (!clients.length) {
    return (
      <ClientPortalLayout tab={tab} onTab={setTab}>
        <div className="cp-card"><div className="cp-empty">
          Your account isn’t linked to anything yet. Please contact your XYZ Merchandise representative.
        </div></div>
      </ClientPortalLayout>
    );
  }

  return (
    <ClientPortalLayout tab={tab} onTab={setTab} shipping={shippingOn} onPay={payOk ? startPay : null} /* @@PORTAL_PAY_v124_1@@ */ inventory={hasStore} /* @@REPORT_REVIEW_v115@@ */ payment={needsPay} /* @@PAYMENT_ACCESS_v125@@ */ equipment={leaseInfo.count > 0 || tab === 'equipment'} /* @@EQUIPMENT_LEASES_v87@@ */ techpacks={tpInfo.count > 0 || tab === 'techpacks'} /* @@TECH_PACKS_v94@@ */>
      <div className="cp-switchrow">
        <label className="cp-switch-label">Account</label>
        <select className="cp-select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      {err && <div className="cp-card"><div className="cp-empty">{err}</div></div>}
      {/* @@EQUIPMENT_LEASES_v87@@ */}
      {leaseInfo.awaiting > 0 && tab !== 'equipment' && (
        <div className="cp-alert" style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 20 }}>
          <span><strong>An equipment lease is waiting for you.</strong> Please review the terms and accept them before the equipment ships.</span>
          <button className="cp-btn-sm" onClick={() => setTab('equipment')}>Review lease</button>
        </div>
      )}
      {/* @@PORTAL_ESTIMATES_v93@@ */}
      {estAwaiting > 0 && tab !== 'estimates' && (
        <div className="cp-alert" style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 20 }}>
          <span><strong>{estAwaiting === 1 ? 'An estimate is' : `${estAwaiting} estimates are`} waiting for your approval.</strong> Review the details and approve or decline.</span>
          <button className="cp-btn-sm" onClick={() => setTab('estimates')}>Review estimates</button>
        </div>
      )}
      {/* @@TECH_PACKS_v94@@ */}
      {tpInfo.awaiting > 0 && tab !== 'techpacks' && (
        <div className="cp-alert" style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 20 }}>
          <span><strong>{tpInfo.awaiting === 1 ? 'A tech pack is' : `${tpInfo.awaiting} tech packs are`} waiting for your review.</strong> Check the artwork, placement and ink colors, then approve or request changes.</span>
          <button className="cp-btn-sm" onClick={() => setTab('techpacks')}>Review tech packs</button>
        </div>
      )}
      {/* @@INVENTORY_TAB_v1@@ */}
      {tab === 'dashboard' && <DashboardTab clientId={clientId} onErr={setErr} onGoPayment={() => setTab('payment')} onGoInvoices={() => setTab('invoices')} />}
      {tab === 'statement' && <StatementTab clientId={clientId} />}
      {/* @@PORTAL_INVOICES_v83@@ */}
      {tab === 'invoices' && <InvoicesTab clientId={clientId} autoPay={autoPay} onAutoPayDone={clearAutoPay} /* @@PORTAL_PAY_v124_1@@ */ />}
      {/* @@PORTAL_ESTIMATES_v93@@ */}
      {tab === 'estimates' && <EstimatesTab clientId={clientId} onChanged={loadEstInfo} />}
      {/* @@TECH_PACKS_v94@@ */}
      {tab === 'techpacks' && <TechPacksTab clientId={clientId} onChanged={loadTpInfo} />}
      {tab === 'inventory' && <InventoryTab clientId={clientId} />}
      {/* @@SHIPPING_TAB_v2@@ */}
      {tab === 'shipping' && <ShippingTab clientId={clientId} />}
      {/* @@QUICKQUOTE_TAB_v1@@ */}
      {tab === 'quickquote' && <QuickQuoteTab />}
      {/* @@PAYMENT_INFO_v82@@ */}
      {tab === 'payment' && needsPay && <PaymentInfoPortalTab clientId={clientId} />}
      {/* @@EQUIPMENT_LEASES_v87@@ */}
      {tab === 'equipment' && <LeasesTab clientId={clientId} onChanged={loadLeaseInfo} />}
    </ClientPortalLayout>
  );
}

// ── Dashboard tab ──────────────────────────────────────────────────────────
function DashboardTab({ clientId, onErr, onGoPayment, onGoInvoices }) { // @@DASH_OPEN_INVOICES_v84@@
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [crBusy, setCrBusy] = useState(''); // @@CREDIT_REQUEST_CLIENT_v1@@

  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    apiFetch(`/api/portal/dashboard?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { setData(d); onErr && onErr(''); })
      .catch(() => { onErr && onErr('Could not load dashboard for this account.'); setData(null); })
      .finally(() => setLoading(false));
  }, [clientId, onErr]);
  useEffect(() => { load(); }, [load]);

  async function downloadReportPdf(reportId, reference) { // @@CLIENT_PORTAL_v2@@
    const res = await apiFetch(`/api/portal/reports/${reportId}/pdf?client_id=${encodeURIComponent(clientId)}`);
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = (reference ? String(reference).replace(/[^A-Za-z0-9._-]/g, '_') : 'report') + '.pdf'; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  }

  if (loading && !data) return <div className="cp-card"><div className="cp-empty">Loading…</div></div>;
  if (!data) return null;

  const mtd = data.mtd;
  const hasStore = data.has_store !== false; // @@REPORT_REVIEW_v115@@
  const needsPay = data.needs_payment_info !== false; // @@PAYMENT_ACCESS_v125@@
  // @@CREDIT_REQUEST_CLIENT_v1@@ request a credit payout or apply-to-invoices; creates an internal task.
  async function requestCredit(action) {
    setCrBusy(action);
    try {
      const res = await apiFetch('/api/portal/credit-request', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, action }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        window.alert('Request submitted. Our team will follow up shortly.');
      } else {
        if (d.code === 'payment_info_missing') { // @@PAYMENT_INFO_v82_1@@
          if (window.confirm(d.error + '\n\nGo to Payment Info now?') && onGoPayment) onGoPayment();
        } else {
          window.alert(d.error || 'Could not submit your request. Please try again.');
        }
      }
    } catch (e) {
      window.alert('Could not submit your request. Please try again.');
    } finally {
      setCrBusy('');
    }
  }

  return (
    <>
      {/* @@PAYMENT_INFO_v82_1@@ W9 / payment-info reminder */}
      {needsPay && <PaymentInfoReminder clientId={clientId} onGo={onGoPayment} />}{/* @@PAYMENT_ACCESS_v125@@ */}
      <div className="cp-stats">
        {hasStore && (<>{/* @@REPORT_REVIEW_v115@@ store cards only when a Shopify store is connected */}
        <div className="cp-stat">
          <div className="cp-k">Month-to-date sales</div>
          <div className="cp-v">{mtd ? money(mtd.net_sales) : '—'}</div>
          {mtd && mtd.store_label && <div className="cp-sub">{mtd.store_label}</div>}
        </div>
        <div className="cp-stat">
          <div className="cp-k">MTD orders</div>
          <div className="cp-v">{mtd ? (mtd.orders ?? '—') : '—'}</div>
        </div>
        </>)}
        {/* @@CLIENT_PORTAL_v2@@ two-number balance */}
        <div className="cp-stat">
          <div className="cp-k">Account balance</div>
          <div className="cp-v">{data.zoho_linked ? money(data.balance, data.balance_currency) : '—'}</div>
          {data.zoho_linked
            ? <div className="cp-sub">Unused credits: {money(data.unused_credits, data.balance_currency)}</div>
            : <div className="cp-sub">Not linked to billing</div>}
          {/* @@CREDIT_REQUEST_CLIENT_v1@@ credit-action buttons */}
          {/* @@CREDIT_REQUEST_v2@@ always show both buttons; server enforces guards + alerts. */}
          <div className="cp-credit-actions" style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button className="cp-btn-sm" disabled={!!crBusy}
                onClick={() => requestCredit('payout')}>{crBusy === 'payout' ? 'Submitting…' : 'Request Credit Payout'}</button>
              <button className="cp-btn-sm" disabled={!!crBusy}
                onClick={() => requestCredit('apply')}>{crBusy === 'apply' ? 'Submitting…' : 'Apply Credit to Invoices'}</button>
            </div>
        </div>
      </div>

      {/* @@DASH_OPEN_INVOICES_v84@@ */}
      <OpenInvoicesCard clientId={clientId} onViewAll={onGoInvoices} />
      {hasStore && (<div className="cp-card">{/* @@REPORT_REVIEW_v115@@ */}
        <div className="cp-card-head"><h2>Report history</h2></div>
        <table className="cp-table">
          {/* @@REPORT_PAYOUT_COL_v1@@ */}
          <thead><tr><th>Reference</th><th>Period</th><th>Generated</th><th>Sales</th><th>Artist payout</th><th></th></tr></thead>
          <tbody>
            {(data.reports || []).map((r) => (
              <tr key={r.id}>
                <td className="cp-ref">{r.reference || '—'}</td>
                <td>{periodLabel(r.reference, r.period_type)}</td>
                <td>{fmtDate(r.generated_at)}</td>
                <td>{money(r.net_sales)}</td>
                <td>{r.artist_due != null ? money(r.artist_due) : '—'}</td>
                <td><button className="cp-btn-sm" onClick={() => downloadReportPdf(r.id, r.reference)}>PDF</button></td>
              </tr>
            ))}
            {(!data.reports || data.reports.length === 0) && (
              <tr><td className="cp-empty" colSpan={6}>No reports yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>)}
    </>
  );
}

// ── Statement tab ──────────────────────────────────────────────────────────
// @@QUICKQUOTE_TAB_v75@@ server-priced Quick Quote — skinny single-column
// layout (Rob's reference). Catalog + all pricing live on the server
// (/api/portal/quote*); only customer-facing numbers render here.
// Includes the v74.1 not-an-official-quote disclaimer copy.
function QuickQuoteTab() {
  const [catalog, setCatalog] = useState(null); // null=loading, {available,...}
  const [category, setCategory] = useState('');
  const [garment, setGarment] = useState('');
  const [orderType, setOrderType] = useState('new');
  const [printLoc, setPrintLoc] = useState('front');
  const [qty, setQty] = useState('');
  const [frontColors, setFrontColors] = useState('');
  const [backColors, setBackColors] = useState('');
  const [discharge, setDischarge] = useState(false);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiFetch('/api/portal/quote/catalog')
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        setCatalog(d);
        if (d && d.available) {
          for (const c of d.categories) {
            const def = c.garments.find((g) => g.is_default);
            if (def) { setCategory(c.name); setGarment(def.name); break; }
          }
        }
      })
      .catch(() => setCatalog({ available: false }));
  }, []);

  const cats = (catalog && catalog.available && catalog.categories) || [];
  const curCat = cats.find((c) => c.name === category);
  const garments = curCat ? curCat.garments : [];
  const g = garments.find((x) => x.name === garment) || null;
  const dischargeOk = !!(g && g.discharge_available);

  function clearQuote() { setQuote(null); setError(''); }
  function pickCategory(name) { setCategory(name); setGarment(''); setDischarge(false); clearQuote(); }
  function pickGarment(name) {
    setGarment(name); clearQuote();
    const row = garments.find((x) => x.name === name);
    if (!row || !row.discharge_available) setDischarge(false);
  }
  function pickLoc(loc) { setPrintLoc(loc); clearQuote(); if (loc === 'front') setBackColors(''); }
  function resetForm() {
    setGarment(''); setOrderType('new'); setPrintLoc('front');
    setQty(''); setFrontColors(''); setBackColors(''); setDischarge(false);
    clearQuote();
  }

  async function getQuote() {
    setError(''); setBusy(true); setQuote(null);
    try {
      const res = await apiFetch('/api/portal/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          garment,
          order_type: orderType,
          print_config: printLoc,
          qty: qty === '' ? null : Number(qty),
          front_colors: frontColors === '' ? 0 : Number(frontColors),
          back_colors: printLoc === 'front' ? 0 : (backColors === '' ? 0 : Number(backColors)),
          discharge,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error || 'Could not get a quote. Try again.'); return; }
      setQuote(d);
    } catch (e) {
      setError('Could not reach the server. Try again.');
    } finally { setBusy(false); }
  }

  function onEnter(e) { if (e.key === 'Enter') getQuote(); }
  const fmt = (n) => '$' + Number(n).toFixed(2);

  const sec = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--cp-muted)', fontWeight: 700, margin: '24px 0 2px' };
  const lbl = { display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 6px' };
  const wide = { width: '100%', height: 42, minWidth: 0 };
  const seg = (active) => ({
    flex: 1, height: 40, borderRadius: 9, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
    border: '1px solid ' + (active ? 'var(--cp-green)' : 'var(--cp-line-strong)'),
    background: active ? 'var(--cp-green)' : '#fff',
    color: active ? '#fff' : 'var(--cp-muted)',
  });
  const bannerBox = (bg, border, color) => ({
    marginTop: 14, padding: '12px 14px', borderRadius: 10, fontSize: 13,
    lineHeight: 1.5, background: bg, border: '1px solid ' + border, color,
  });
  const row = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--cp-line)', fontSize: 14 };
  const divider = { borderTop: '1px solid var(--cp-line)', margin: '22px 0 0' };

  if (catalog === null) {
    return <div className="cp-card" style={{ maxWidth: 480, margin: '0 auto 20px' }}><div className="cp-empty">Loading garment catalog…</div></div>;
  }
  if (!catalog.available) {
    return <div className="cp-card" style={{ maxWidth: 480, margin: '0 auto 20px' }}><div className="cp-empty">The garment catalog is temporarily unavailable. Please try again in a few minutes.</div></div>;
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div className="cp-card">
        <div style={{ padding: '26px 24px 24px' }}>
          <h2 style={{ fontFamily: "'Archivo Expanded', 'Archivo', sans-serif", fontWeight: 700, fontSize: 20, margin: 0 }}>Get an Instant Quote</h2>
          <p style={{ color: 'var(--cp-muted)', fontSize: 14, margin: '6px 0 0' }}>Fill in your order details below to see your price breakdown.</p>

          <div style={sec}>Garment</div>
          <label style={lbl}>Category</label>
          <select className="cp-select" style={wide} value={category} onChange={(e) => pickCategory(e.target.value)}>
            <option value="">Select a category…</option>
            {cats.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <label style={lbl}>Garment</label>
          <select className="cp-select" style={wide} value={garment} disabled={!category} onChange={(e) => pickGarment(e.target.value)}>
            <option value="">{category ? 'Select a garment…' : 'Select a category first'}</option>
            {garments.map((x) => <option key={x.name} value={x.name}>{x.name}</option>)}
          </select>
          {g && (g.weight || g.fabric || g.description) && (
            <div style={{ marginTop: 12, padding: '12px 14px', background: '#f6f7f9', borderRadius: 10, fontSize: 13, lineHeight: 1.6 }}>
              {g.weight && <div><strong>Weight:</strong> {g.weight} oz</div>}
              {g.fabric && <div><strong>Fabric:</strong> {g.fabric}</div>}
              {g.description && <div><strong>Garment Info:</strong> {g.description}</div>}
            </div>
          )}

          <div style={divider} />
          <div style={sec}>Order Details</div>
          <label style={lbl}>Order Type</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={seg(orderType === 'new')} onClick={() => { setOrderType('new'); clearQuote(); }}>New Order</button>
            <button type="button" style={seg(orderType === 'reorder')} onClick={() => { setOrderType('reorder'); clearQuote(); }}>Reorder</button>
          </div>
          <label style={lbl}>Print Configuration</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={seg(printLoc === 'front')} onClick={() => pickLoc('front')}>Front Print Only</button>
            <button type="button" style={seg(printLoc === 'front_back')} onClick={() => pickLoc('front_back')}>Front + Back Print</button>
          </div>

          <div style={sec}>Production Options</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, padding: '11px 14px', border: '1px solid var(--cp-line-strong)', borderRadius: 10, fontSize: 14, color: dischargeOk ? 'var(--cp-txt)' : 'var(--cp-muted)', cursor: dischargeOk ? 'pointer' : 'default' }}>
            <input type="checkbox" disabled={!dischargeOk} checked={discharge}
              onChange={(e) => { setDischarge(e.target.checked); clearQuote(); }} />
            Print with Discharge Ink{!dischargeOk && g ? ' (not available for this garment)' : ''}
          </label>
          {discharge && (
            <div style={{ marginTop: 8, fontSize: 13, color: 'var(--cp-muted)' }}>
              <strong>Discharge printing does not require a white underbase. Enter only the visible ink colors.</strong>
            </div>
          )}

          <label style={lbl}>Quantity</label>
          <input className="cp-input" style={wide} type="number" min="72" placeholder="Enter quantity (72+)" value={qty}
            onChange={(e) => { setQty(e.target.value); clearQuote(); }} onKeyDown={onEnter} />
          <label style={lbl}>Front Colors</label>
          <input className="cp-input" style={wide} type="number" min="0" max="12" placeholder="0–12" value={frontColors}
            onChange={(e) => { setFrontColors(e.target.value); clearQuote(); }} onKeyDown={onEnter} />
          {printLoc === 'front_back' && (
            <>
              <label style={lbl}>Back Colors</label>
              <input className="cp-input" style={wide} type="number" min="0" max="12" placeholder="0–12" value={backColors}
                onChange={(e) => { setBackColors(e.target.value); clearQuote(); }} onKeyDown={onEnter} />
            </>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
            <button type="button" disabled={busy} onClick={getQuote}
              style={{ flex: 1, height: 44, borderRadius: 10, border: 'none', background: 'var(--cp-green)', color: '#fff', fontFamily: 'inherit', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: busy ? .6 : 1 }}>
              {busy ? 'Calculating…' : 'Get Quote'}
            </button>
            <button type="button" onClick={resetForm}
              style={{ flex: 1, height: 44, borderRadius: 10, background: '#fff', border: '1px solid var(--cp-green)', color: 'var(--cp-green)', fontFamily: 'inherit', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              Reset
            </button>
          </div>

          {!quote && (
            <div style={bannerBox('#f6f7f9', 'var(--cp-line-strong)', 'var(--cp-txt)')}>
              Minimum order is <strong>72 pieces</strong>. Orders under 120 pieces are limited to <strong>4 colors per location</strong>. Pricing shown is an estimate to help you plan — it is not an official quote. Contact your XYZ rep for a formal quote.
            </div>
          )}
          {error && (
            <div role="alert" style={bannerBox('rgba(220,38,38,.06)', 'rgba(220,38,38,.3)', '#b91c1c')}>{error}</div>
          )}
        </div>
      </div>

      {quote && (
        <div className="cp-card">
          <div style={{ padding: '22px 24px' }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>Your Quote</h2>
            <div style={row}><span>Price Per Piece</span><strong>{fmt(quote.price_per_piece)}/pc</strong></div>
            <div style={row}><span>Production Subtotal</span><strong>{fmt(quote.production_subtotal)}</strong></div>
            <div style={row}>
              <span>{quote.setup.label}</span>
              <strong>{fmt(quote.setup_cost)} ({quote.setup.colors} × {fmt(quote.setup.fee_per_color)})</strong>
            </div>
            <div style={{ ...row, borderBottom: 'none', fontSize: 16 }}>
              <span style={{ fontWeight: 700 }}>Order Total</span>
              <strong style={{ fontFamily: "'Archivo Expanded', 'Archivo', sans-serif", color: 'var(--cp-green)' }}>{fmt(quote.order_total)}</strong>
            </div>

            {quote.banner === 'small_run' && (
              <div style={bannerBox('rgba(232,193,90,.1)', 'rgba(232,193,90,.4)', '#8a6d1a')}>
                This order qualifies for our small-run program. Orders between 72 and 119 pieces are limited to 4 colors per print location. For best pricing, we recommend ordering 120+ pieces.
              </div>
            )}
            {quote.banner === 'standard' && (
              <div style={bannerBox('rgba(31,157,87,.07)', 'rgba(31,157,87,.35)', 'var(--cp-green)')}>
                Need artwork review, garment recommendations, or delivery information? Contact us to finalize your project.
              </div>
            )}
            {quote.banner === 'large_order' && (
              <div style={bannerBox('rgba(110,168,255,.08)', 'rgba(110,168,255,.35)', '#1d4ed8')}>
                <strong>Large Order Inquiry</strong><br />
                Orders over 1,200 pieces typically qualify for custom pricing. Contact us for a custom quote and production recommendations.
              </div>
            )}

            {/* @@QUICKQUOTE_DISCLAIMER_v74_1@@ (carried forward) */}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--cp-line)', fontSize: 12, color: 'var(--cp-muted)', lineHeight: 1.5 }}>
              This calculator helps you build an idea of pricing — the numbers above are an estimate and do not serve as an official quote. Please contact your XYZ Merchandise rep for a formal quote on your project.
            </div>
          </div>
        </div>
      )}

      {quote && quote.next_break && (
        <div className="cp-card" style={{ borderLeft: '3px solid var(--cp-green)' }}>
          <div style={{ padding: '22px 24px' }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>Next Price Break</h2>
            <div style={row}>
              <span>Quantity</span>
              <strong>{quote.next_break.qty} pcs <span style={{ color: 'var(--cp-muted)', fontWeight: 400 }}>(+{quote.next_break.additional} additional pieces)</span></strong>
            </div>
            <div style={row}><span>Price Per Piece</span><strong>{fmt(quote.next_break.price_per_piece)}/pc</strong></div>
            <div style={{ ...row, borderBottom: 'none' }}><span>Order Total</span><strong>{fmt(quote.next_break.order_total)}</strong></div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShippingTab({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parcels, setParcels] = useState([{ weight_lbs: '', length_in: '', width_in: '', height_in: '' }]);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    apiFetch(`/api/portal/shipping?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setData(d)).catch(() => setData({ available: false }))
      .finally(() => setLoading(false));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  function setP(i, k, v) { setParcels((ps) => ps.map((p, idx) => idx === i ? { ...p, [k]: v } : p)); }
  function addP() { setParcels((ps) => [...ps, { weight_lbs: '', length_in: '', width_in: '', height_in: '' }]); }
  function rmP(i) { setParcels((ps) => ps.length > 1 ? ps.filter((_, idx) => idx !== i) : ps); }
  // @@PORTAL_PRESETS_v1@@ fill a row from a shared global parcel preset.
  function applyPreset(i, presetId) {
    const list = (data && data.presets) || [];
    const pr = list.find((x) => String(x.id) === String(presetId));
    if (!pr) return;
    setP(i, 'weight_lbs', pr.weight_lbs == null ? '' : String(pr.weight_lbs));
    setP(i, 'length_in', pr.length_in == null ? '' : String(pr.length_in));
    setP(i, 'width_in', pr.width_in == null ? '' : String(pr.width_in));
    setP(i, 'height_in', pr.height_in == null ? '' : String(pr.height_in));
  }
  async function doCreate() {
    setMsg('');
    // @@SHIP_DESC_REQUIRED_v79@@ mirrors the server-side 400 — the server is still
    // the real enforcement point, this just avoids a wasted round trip.
    if (!description.trim()) { setMsg('A description is required.'); return; }
    setBusy(true);
    try {
      const res = await apiFetch('/api/portal/shipping/label', { method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, parcels, description }) });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setMsg('Label created.'); setParcels([{ weight_lbs: '', length_in: '', width_in: '', height_in: '' }]); setDescription(''); load(); }
      else { setMsg(d.error || 'Could not create label.'); }
    } catch (e) { setMsg('Could not reach the server.'); } finally { setBusy(false); }
  }
  function addrText(a) { if (!a) return '—'; return [a.name, a.street1, a.street2, [a.city, a.state, a.postal].filter(Boolean).join(', ')].filter(Boolean).join('\n'); }
  function dlLabel(id) {
    apiFetch(`/api/portal/shipping/label/${id}/pdf`).then(async (res) => {
      if (!res.ok) return; const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `label-${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    });
  }
  if (loading && !data) return <div className="cp-card"><div className="cp-empty">Loading…</div></div>;
  if (!data || !data.available) return <div className="cp-card"><div className="cp-empty">Shipping isn’t enabled for this account.</div></div>;
  return (
    <>
      {/* @@SANDBOX_BANNER_v1@@ */}
      {data.sandbox && (
        <div className="cp-alert">
          <strong>Sandbox mode.</strong> You can generate labels here, but they are test labels — not valid for actual shipping.
        </div>
      )}
      <div className="cp-card">
        <div className="cp-card-head"><h2>Create a shipping label</h2><span className="cp-sub">FedEx · ref {data.reference || '—'}</span></div>
        {/* @@SHIPPING_STYLES_v1@@ */}
        <div className="cp-ship-grid">
          <div className="cp-ship-addr"><div className="cp-k">From</div><pre>{addrText(data.from)}</pre></div>
          <div className="cp-ship-addr"><div className="cp-k">To</div><pre>{addrText(data.to)}</pre></div>
        </div>
        <div className="cp-ship-body"><input className="cp-input cp-ship-desc" placeholder="Description (required — e.g. tour tees)" value={description} onChange={(e) => setDescription(e.target.value)} required /></div>
        <div className="cp-ship-body"><table className="cp-table cp-ship-table">
          <thead><tr><th>Preset</th><th>Weight (lb)</th><th>L (in)</th><th>W (in)</th><th>H (in)</th><th></th></tr></thead>
          <tbody>
            {parcels.map((p, i) => (
              <tr key={i}>
                <td>
                  <select className="cp-select" value="" onChange={(e) => { applyPreset(i, e.target.value); e.target.value = ''; }}>
                    <option value="">Parcel preset…</option>
                    {((data && data.presets) || []).map((pr) => (<option key={pr.id} value={pr.id}>{pr.name}</option>))}
                  </select>
                </td>
                <td><input className="cp-input" type="number" value={p.weight_lbs} onChange={(e) => setP(i, 'weight_lbs', e.target.value)} /></td>
                <td><input className="cp-input" type="number" value={p.length_in} onChange={(e) => setP(i, 'length_in', e.target.value)} /></td>
                <td><input className="cp-input" type="number" value={p.width_in} onChange={(e) => setP(i, 'width_in', e.target.value)} /></td>
                <td><input className="cp-input" type="number" value={p.height_in} onChange={(e) => setP(i, 'height_in', e.target.value)} /></td>
                <td>{parcels.length > 1 && <button className="cp-remove" aria-label="Remove box" onClick={() => rmP(i)}>×</button>}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="cp-ship-actions">
          <button className="cp-btn-sm" onClick={addP}>+ Add box (MPS)</button>
          <button className="cp-btn-sm" onClick={doCreate} disabled={busy || !description.trim()}>{busy ? 'Creating…' : 'Create label'}</button>
        </div>
        {msg && <p className="cp-msg">{msg}</p>}
      </div>
      <div className="cp-card">
        <div className="cp-card-head"><h2>Shipping history</h2></div>
        <table className="cp-table">
          <thead><tr><th>Label</th><th>Tracking</th><th>Created</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {(data.labels || []).map((l) => (
              <tr key={l.id}>
                <td>{l.label || '—'}</td>
                <td>{l.tracking_number}</td>
                <td>{fmtDate(l.created_at)}</td>
                <td>{l.status}</td>
                <td>{l.has_pdf && <button className="cp-btn-sm" onClick={() => dlLabel(l.id)}>PDF</button>}</td>
              </tr>
            ))}
            {(!data.labels || data.labels.length === 0) && (
              <tr><td className="cp-empty" colSpan={5}>No shipments yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function InventoryTab({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    apiFetch(`/api/portal/inventory?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setData(d))
      .catch(() => setData({ available: false, reason: 'error' }))
      .finally(() => setLoading(false));
  }, [clientId]);
  // @@INVENTORY_PRINTCSV_v1@@ CSV (RFC-4180 quoting) + print, client-side only.
  function csvCell(v) {
    const s = (v == null ? '' : String(v));
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }
  function downloadCsv() {
    if (!data || !data.items) return;
    const head = ['Product', 'SKU', 'Stock', 'Price'];
    const lines = [head.join(',')];
    for (const it of data.items) {
      lines.push([csvCell(it.title), csvCell(it.sku), csvCell(it.on_hand),
        csvCell(it.price != null ? Number(it.price).toFixed(2) : '')].join(','));
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const label = (data.store_label || 'inventory').replace(/[^A-Za-z0-9._-]/g, '_');
    a.href = url; a.download = `inventory-${label}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  }
  function printInventory() { window.print(); }
  return (
    <div className="cp-card cp-print-area">
      <div className="cp-card-head">
        <div>
          <h2>Inventory</h2>
          {data && data.available && data.store_label && <div className="cp-sub">{data.store_label}</div>}
        </div>
        {data && data.available && (data.items || []).length > 0 && (
          <div className="cp-noprint" style={{ display: 'flex', gap: 8 }}>
            <button className="cp-btn-sm" onClick={downloadCsv}>CSV</button>
            <button className="cp-btn-sm" onClick={printInventory}>Print</button>
          </div>
        )}
      </div>
      {loading && !data ? (
        <div className="cp-empty">Loading…</div>
      ) : !data || !data.available ? (
        <div className="cp-empty">
          {data && data.reason === 'no_store' ? 'No store connected yet.'
            : data && data.reason === 'no_creds' ? 'Store credentials not configured.'
            : 'Inventory is temporarily unavailable. Please try again shortly.'}
        </div>
      ) : (
        <>
          <table className="cp-table">
            <thead><tr><th>Product</th><th>SKU</th><th style={{textAlign:'right'}}>Stock</th><th style={{textAlign:'right'}}>Price</th></tr></thead>
            <tbody>
              {(data.items || []).map((it, i) => (
                <tr key={i}>
                  <td>{it.title || '—'}</td>
                  <td>{it.sku || '—'}</td>
                  <td style={{textAlign:'right'}}>{it.on_hand}</td>
                  <td style={{textAlign:'right'}}>{it.price != null ? money(it.price) : '—'}</td>
                </tr>
              ))}
              {(!data.items || data.items.length === 0) && (
                <tr><td className="cp-empty" colSpan={4}>No active products.</td></tr>
              )}
            </tbody>
          </table>
          {data.truncated && <p className="cp-sub" style={{ marginTop: 10 }}>Showing the first 1000 items.</p>}
        </>
      )}
    </div>
  );
}

function StatementTab({ clientId }) {
  const [period, setPeriod] = useState('mtd');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    apiFetch(`/api/portal/statement?client_id=${encodeURIComponent(clientId)}&period=${period}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setData(d))
      .catch(() => setData({ available: false, reason: 'error' }))
      .finally(() => setLoading(false));
  }, [clientId, period]);
  useEffect(() => { load(); }, [load]);

  async function downloadStatementPdf() {
    const res = await apiFetch(`/api/portal/statement.pdf?client_id=${encodeURIComponent(clientId)}&period=${period}`);
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    // @@STMT_FILENAME_RANGE_CLIENT_v1@@ XYZ-Account-Statement-<period>-<from>_to_<to>-<designation>.pdf
    const fromD = (data && data.from) ? String(data.from) : '';
    const toD = (data && data.to) ? String(data.to) : '';
    const range = fromD + '_to_' + toD;
    const desig = (data && data.client && data.client.designation_code ? String(data.client.designation_code) : '').replace(/[^A-Za-z0-9._-]/g, '_');
    const fname = 'XYZ-Account-Statement-' + period + '-' + range + (desig ? '-' + desig : '') + '.pdf';
    a.href = url; a.download = fname; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  }

  return (
    <div className="cp-card">
      <div className="cp-card-head">
        <h2>Account statement</h2>
        <div className="cp-period">
          {['mtd', 'qtd', 'ytd'].map((p) => (
            <button key={p} className={period === p ? 'cp-pill cp-pill-on' : 'cp-pill'} onClick={() => setPeriod(p)}>
              {p.toUpperCase()}
            </button>
          ))}
          <button className="cp-btn-sm" onClick={downloadStatementPdf} disabled={!data || !data.available}>Download PDF</button>
        </div>
      </div>

      {loading && !data ? (
        <div className="cp-empty">Loading…</div>
      ) : !data || !data.available ? (
        <div className="cp-empty">
          {data && data.reason === 'not_linked'
            ? 'This account isn’t linked to billing yet.'
            : 'Statement is temporarily unavailable. Please try again shortly.'}
        </div>
      ) : (
        <>
          <div className="cp-stmt-summary">
            <div><span className="cp-k">Opening</span><span className="cp-v2">{money(data.statement.opening_balance, data.statement.currency_code)}</span></div>
            <div><span className="cp-k">Closing</span><span className="cp-v2">{money(data.statement.closing_balance, data.statement.currency_code)}</span></div>
            <div><span className="cp-k">Period</span><span className="cp-v2">{fmtDate(data.from)} – {fmtDate(data.to)}</span></div>
          </div>
          <table className="cp-table">
            {/* @@ROYALTY_STMT_UI@@ */}
            {/* @@DEBIT_CREDIT_COLS_v1@@ */}
            <thead><tr><th>Date</th><th>Type</th><th>Details</th><th style={{textAlign:'right'}}>Credit</th><th style={{textAlign:'right'}}>Debit</th><th style={{textAlign:'right'}}>Balance</th></tr></thead>
            <tbody>
              {(data.statement.transactions || []).map((t, i) => (
                <tr key={i} style={t.in_balance === false ? { opacity: 0.6 } : undefined}>
                  <td>{fmtDate(t.date)}</td>
                  <td>{t.type || '—'}</td>
                  <td>{t.details || '—'}</td>
                  <td style={{textAlign:'right'}}>{t.owed != null ? money(t.owed, data.statement.currency_code) : '—'}</td>
                  <td style={{textAlign:'right'}}>{t.paid != null ? money(t.paid, data.statement.currency_code) : '—'}</td>
                  {/* @@APP_CONTEXT_UI_v6@@ */}<td style={{textAlign:'right'}}>{t.in_balance === false ? <span style={{color:'var(--cp-muted)',fontSize:12}}>context</span> : money(t.running_balance, data.statement.currency_code)}</td>
                </tr>
              ))}
              {(!data.statement.transactions || data.statement.transactions.length === 0) && (
                <tr><td className="cp-empty" colSpan={6}>No transactions in this period.</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
